import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Plus, 
  Trash2, 
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Building2,
  User,
  ChevronDown,
  X,
  Loader2,
  Settings
} from 'lucide-react';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

const CREDENTIAL_TYPES = [
  { id: 'background_check', name: 'Background Check' },
  { id: 'drug_screen', name: 'Drug Screen' },
  { id: 'dmv_record', name: 'DMV/MVR Record' },
  { id: 'drivers_license', name: "Driver's License" },
  { id: 'medical_certificate', name: 'Medical Certificate' },
  { id: 'insurance_coi', name: 'Certificate of Insurance' },
  { id: 'dot_authority', name: 'DOT Authority' },
  { id: 'business_license', name: 'Business License' },
  { id: 'npi_number', name: 'NPI Number' },
  { id: 'vehicle_registration', name: 'Vehicle Registration' },
  { id: 'vehicle_inspection', name: 'Vehicle Inspection' }
];

export const TrustManagement = ({ onClose }) => {
  const { user, organization } = useExchangeAuth();
  const [trustRelationships, setTrustRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadTrustRelationships();
  }, []);

  const loadTrustRelationships = async () => {
    try {
      const res = await exchangeApi.getTrustRelationships();
      setTrustRelationships(res.data.trust_relationships || []);
    } catch (error) {
      console.error('Failed to load trust relationships:', error);
      toast.error('Failed to load trusted partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      // Search for organizations
      const res = await exchangeApi.searchOrganizations({
        verified_only: false // Include unverified for trust setup
      });
      
      // Filter by search query
      const filtered = (res.data.organizations || []).filter(org => 
        org.legal_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.dba_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      // Exclude already trusted
      const trustedIds = trustRelationships.map(t => t.trusted_id);
      const available = filtered.filter(org => !trustedIds.includes(org.id));
      
      setSearchResults(available);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleDeleteTrust = async (trustId) => {
    if (!window.confirm('Are you sure you want to remove this trusted partner?')) {
      return;
    }
    
    setDeleting(trustId);
    try {
      await exchangeApi.deleteTrust(trustId);
      setTrustRelationships(prev => prev.filter(t => t.id !== trustId));
      toast.success('Trusted partner removed');
    } catch (error) {
      console.error('Failed to delete trust:', error);
      toast.error('Failed to remove trusted partner');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-neon-green/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-neon-green" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Trust Relationships</h3>
            <p className="text-sm text-slate-400">Auto-approve credential requests from trusted partners</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary"
            data-testid="add-trust-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Partner
          </button>
          {onClose && (
            <button onClick={onClose} className="text-slate-400 hover:text-white p-2">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Trust List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-neon-green animate-spin" />
        </div>
      ) : trustRelationships.length > 0 ? (
        <div className="space-y-4">
          {trustRelationships.map((trust) => (
            <TrustCard 
              key={trust.id} 
              trust={trust} 
              onDelete={() => handleDeleteTrust(trust.id)}
              deleting={deleting === trust.id}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h4 className="text-lg text-white font-medium mb-2">No Trusted Partners</h4>
          <p className="text-slate-400 mb-4">
            Add trusted partners to auto-approve their credential requests
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-secondary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Partner
          </button>
        </div>
      )}

      {/* Add Trust Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddTrustModal
            onClose={() => {
              setShowAddModal(false);
              setSearchResults([]);
              setSearchQuery('');
            }}
            onSuccess={(newTrust) => {
              setTrustRelationships(prev => [...prev, newTrust]);
              setShowAddModal(false);
              setSearchResults([]);
              setSearchQuery('');
            }}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchResults={searchResults}
            onSearch={handleSearch}
            searching={searching}
            existingTrustIds={trustRelationships.map(t => t.trusted_id)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const TrustCard = ({ trust, onDelete, deleting }) => {
  const [expanded, setExpanded] = useState(false);
  
  const isExpired = trust.expires_at && new Date(trust.expires_at) < new Date();
  
  return (
    <motion.div
      layout
      className={`p-4 rounded-lg border transition-colors ${
        isExpired 
          ? 'bg-red-500/5 border-red-500/30' 
          : trust.is_active 
            ? 'bg-white/5 border-slate-700 hover:border-slate-600'
            : 'bg-white/5 border-slate-700 opacity-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isExpired ? 'bg-red-500/20' : 'bg-neon-green/20'
          }`}>
            <Building2 className={`w-5 h-5 ${isExpired ? 'text-red-400' : 'text-neon-green'}`} />
          </div>
          <div>
            <p className="text-white font-medium">{trust.trusted_name}</p>
            <div className="flex items-center space-x-3 text-xs text-slate-400">
              {trust.auto_approve_all ? (
                <span className="text-neon-cyan">All credentials</span>
              ) : (
                <span>{trust.credential_types?.length || 0} credential types</span>
              )}
              {trust.max_requests_per_month > 0 && (
                <span>• {trust.requests_this_month || 0}/{trust.max_requests_per_month} requests</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isExpired ? (
            <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">
              Expired
            </span>
          ) : trust.is_active ? (
            <span className="text-xs px-2 py-1 rounded-full bg-neon-green/20 text-neon-green">
              Active
            </span>
          ) : (
            <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
              Inactive
            </span>
          )}
          
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-slate-400 hover:text-white"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          
          <button
            onClick={onDelete}
            disabled={deleting}
            className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-50"
            data-testid={`delete-trust-${trust.id}`}
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-slate-700"
          >
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-xs mb-1">Created</p>
                <p className="text-slate-300">
                  {new Date(trust.created_at).toLocaleDateString()}
                </p>
              </div>
              {trust.expires_at && (
                <div>
                  <p className="text-slate-500 text-xs mb-1">Expires</p>
                  <p className={isExpired ? 'text-red-400' : 'text-slate-300'}>
                    {new Date(trust.expires_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              {!trust.auto_approve_all && trust.credential_types?.length > 0 && (
                <div className="col-span-2">
                  <p className="text-slate-500 text-xs mb-2">Approved Credential Types</p>
                  <div className="flex flex-wrap gap-2">
                    {trust.credential_types.map((type) => (
                      <span 
                        key={type} 
                        className="text-xs px-2 py-1 rounded bg-white/10 text-slate-300"
                      >
                        {type.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const AddTrustModal = ({ 
  onClose, 
  onSuccess, 
  searchQuery, 
  setSearchQuery, 
  searchResults, 
  onSearch, 
  searching,
  existingTrustIds 
}) => {
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    auto_approve_all: true,
    credential_types: [],
    max_requests_per_month: -1,
    expires_days: null
  });

  const handleCreate = async () => {
    if (!selectedOrg) return;
    
    setCreating(true);
    try {
      const res = await exchangeApi.createTrust({
        trusted_type: 'organization',
        trusted_id: selectedOrg.id,
        trusted_name: selectedOrg.legal_name,
        credential_types: formData.auto_approve_all ? [] : formData.credential_types,
        auto_approve_all: formData.auto_approve_all,
        max_requests_per_month: formData.max_requests_per_month,
        expires_days: formData.expires_days
      });
      
      if (res.data.success) {
        toast.success('Trusted partner added!');
        onSuccess({
          id: res.data.trust_id,
          trusted_name: selectedOrg.legal_name,
          trusted_id: selectedOrg.id,
          auto_approve_all: formData.auto_approve_all,
          credential_types: formData.credential_types,
          max_requests_per_month: formData.max_requests_per_month,
          is_active: true,
          created_at: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to create trust:', error);
      toast.error(error.response?.data?.detail || 'Failed to add trusted partner');
    } finally {
      setCreating(false);
    }
  };

  const toggleCredentialType = (typeId) => {
    setFormData(prev => ({
      ...prev,
      credential_types: prev.credential_types.includes(typeId)
        ? prev.credential_types.filter(t => t !== typeId)
        : [...prev.credential_types, typeId]
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              {step === 1 ? 'Search Organization' : 'Configure Trust'}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Steps */}
          <div className="flex items-center space-x-4 mt-4">
            <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-neon-green' : 'text-slate-500'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step >= 1 ? 'bg-neon-green text-black' : 'bg-slate-700'
              }`}>1</div>
              <span className="text-sm">Select</span>
            </div>
            <div className="flex-1 h-px bg-slate-700" />
            <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-neon-green' : 'text-slate-500'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step >= 2 ? 'bg-neon-green text-black' : 'bg-slate-700'
              }`}>2</div>
              <span className="text-sm">Configure</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-4">
              {/* Search */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                  placeholder="Search by organization name..."
                  className="flex-1 input-dark"
                  data-testid="trust-search-input"
                />
                <button
                  onClick={onSearch}
                  disabled={searching || !searchQuery.trim()}
                  className="btn-primary"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
              </div>

              {/* Results */}
              {searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => setSelectedOrg(org)}
                      className={`w-full p-4 text-left rounded-lg border transition-all ${
                        selectedOrg?.id === org.id
                          ? 'bg-neon-green/10 border-neon-green'
                          : 'bg-white/5 border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`select-org-${org.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{org.legal_name}</p>
                            <p className="text-xs text-slate-400">
                              {org.address_city}, {org.address_state}
                            </p>
                          </div>
                        </div>
                        {org.verified && (
                          <CheckCircle2 className="w-5 h-5 text-neon-green" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : searchQuery && !searching ? (
                <div className="text-center py-8 text-slate-400">
                  <Search className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No organizations found</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Search for an organization to add as a trusted partner</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Selected Org */}
              <div className="p-4 bg-neon-green/10 border border-neon-green/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-neon-green" />
                  <div>
                    <p className="text-white font-medium">{selectedOrg?.legal_name}</p>
                    <p className="text-xs text-slate-400">
                      {selectedOrg?.address_city}, {selectedOrg?.address_state}
                    </p>
                  </div>
                </div>
              </div>

              {/* Auto-approve all */}
              <div>
                <label className="flex items-center justify-between p-4 bg-white/5 rounded-lg cursor-pointer">
                  <div>
                    <p className="text-white font-medium">Auto-approve all credentials</p>
                    <p className="text-xs text-slate-400">
                      Automatically approve all credential requests from this partner
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.auto_approve_all}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_approve_all: e.target.checked }))}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-neon-green focus:ring-neon-green"
                  />
                </label>
              </div>

              {/* Specific credential types */}
              {!formData.auto_approve_all && (
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Select credential types to auto-approve
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {CREDENTIAL_TYPES.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => toggleCredentialType(type.id)}
                        className={`p-2 text-xs rounded-lg border transition-all text-left ${
                          formData.credential_types.includes(type.id)
                            ? 'bg-neon-green/10 border-neon-green text-neon-green'
                            : 'bg-white/5 border-slate-700 text-slate-300 hover:border-slate-600'
                        }`}
                      >
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly limit */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Monthly request limit
                </label>
                <select
                  value={formData.max_requests_per_month}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    max_requests_per_month: parseInt(e.target.value) 
                  }))}
                  className="w-full input-dark"
                >
                  <option value={-1}>Unlimited</option>
                  <option value={10}>10 requests/month</option>
                  <option value={25}>25 requests/month</option>
                  <option value={50}>50 requests/month</option>
                  <option value={100}>100 requests/month</option>
                </select>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Trust expiration
                </label>
                <select
                  value={formData.expires_days || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    expires_days: e.target.value ? parseInt(e.target.value) : null 
                  }))}
                  className="w-full input-dark"
                >
                  <option value="">Never expires</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>6 months</option>
                  <option value={365}>1 year</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700 flex justify-between">
          {step === 1 ? (
            <>
              <button onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedOrg}
                className="btn-primary disabled:opacity-50"
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="btn-secondary">
                Back
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || (!formData.auto_approve_all && formData.credential_types.length === 0)}
                className="btn-primary disabled:opacity-50"
                data-testid="create-trust-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Add Trusted Partner
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TrustManagement;
